import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getUser } from '@/utils/auth';
import { FileText, Calendar, CheckCircle, XCircle, Clock, AlertCircle, Plus, X, Edit2, Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format Date -> 'YYYY-MM-DD' pakai komponen tanggal lokal (bukan toISOString,
// yang bisa geser sehari kalau timezone browser di belakang UTC).
function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultDateRange() {
  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setDate(today.getDate() - 30);
  return {
    fromDate: toDateInputValue(lastMonth),
    toDate: toDateInputValue(today),
  };
}

function emptyFormData() {
  return {
    leave_type: 'cuti',
    start_date: toDateInputValue(new Date()),
    end_date: toDateInputValue(new Date()),
    reason: '',
    document: null,
  };
}

export default function LeaveRequests() {
  const user = getUser();
  const canFilterByEmployee = user?.role === 'admin' || user?.role === 'security_manager' || user?.has_subordinates;
  const canCreateLeave = user?.role === 'employee' || user?.role === 'admin';
  const canApprove = user?.role === 'admin' || user?.has_subordinates;

  const [requests, setRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [{ fromDate, toDate }, setDateRange] = useState(defaultDateRange);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);
  const [submitting, setSubmitting] = useState(false);
  const [documentFileName, setDocumentFileName] = useState('');

  // Re-fetch setiap kali filter employee atau date range berubah (filter di server)
  useEffect(() => {
    fetchLeaveRequests(selectedEmployee, fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, fromDate, toDate]);

  // Pending approvals & daftar employee cuma perlu di-fetch sekali di awal
  useEffect(() => {
    if (user?.role !== 'employee' || user?.has_subordinates) {
      fetchPendingApprovals();
    }
    if (canFilterByEmployee) {
      fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeaveRequests = async (employeeFilter, from, to) => {
    setLoading(true);
    try {
      const params = {};
      if (employeeFilter && employeeFilter !== 'all') {
        params.user_id = employeeFilter;
      }
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const response = await api.get('/leave-requests', { params });
      setRequests(response.data);
    } catch (error) {
      toast.error('Gagal memuat leave requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const response = await api.get('/leave-requests/pending-approvals');
      setPendingApprovals(response.data);
    } catch (error) {
      console.error('Failed to load pending approvals:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/leave-requests/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Gagal memuat daftar employee', error);
    }
  };

  const [editingId, setEditingId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formPayload = new FormData();
      formPayload.append('leave_type', formData.leave_type);
      formPayload.append('start_date', formData.start_date);
      formPayload.append('end_date', formData.end_date);
      formPayload.append('reason', formData.reason);
      if (formData.document) {
        formPayload.append('document', formData.document);
      }

      if (editingId) {
        await api.put(`/leave-requests/${editingId}`, formPayload);
        toast.success('Leave request berhasil diupdate!');
      } else {
        await api.post('/leave-requests', formPayload);
        toast.success('Leave request berhasil diajukan!');
      }

      setFormData(emptyFormData());
      setDocumentFileName('');
      setShowForm(false);
      setEditingId(null);
      fetchLeaveRequests(selectedEmployee, fromDate, toDate);
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan leave request');
    } finally {
      setSubmitting(false);
    }
  };

  const canEditLeave = (request) => {
    if (!request) return false;
    if (request.user_id !== user?.id) return false;
    if (request.status !== 'pending') return false;
    if (!request.created_at) return true;
    const created = new Date(request.created_at);
    const now = new Date();
    const daysDiff = Math.floor((now - created) / (1000 * 60 * 60 * 24));
    return daysDiff <= 30;
  };

  const handleEdit = (request) => {
    if (!canEditLeave(request)) {
      toast.error('Hanya leave request pending milik Anda dalam 30 hari yang bisa diedit');
      return;
    }
    setFormData({
      leave_type: request.leave_type,
      start_date: request.start_date,
      end_date: request.end_date,
      reason: request.reason,
      document: null
    });
    setEditingId(request.id);
    setDocumentFileName(request.document_path || '');
    setShowForm(true);
  };

  const handleDelete = async (requestOrId) => {
    const request = typeof requestOrId === 'object' ? requestOrId : requests.find((r) => r.id === requestOrId);
    if (!canEditLeave(request)) {
      toast.error('Hanya leave request pending milik Anda dalam 30 hari yang bisa dihapus');
      return;
    }
    if (!confirm('Yakin hapus request ini?')) return;
    try {
      await api.delete(`/leave-requests/${request.id}`);
      toast.success('Berhasil dihapus');
      fetchLeaveRequests(selectedEmployee, fromDate, toDate);
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal hapus');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, document: file });
      setDocumentFileName(file.name);
    }
  };

  const handleSupervisorReview = async (leaveId, action) => {
    try {
      await api.put(`/leave-requests/${leaveId}/supervisor-review`, { action });
      toast.success(`Leave request ${action}d!`);
      fetchLeaveRequests(selectedEmployee, fromDate, toDate);
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal review request');
    }
  };

  const handleAdminReview = async (leaveId, action) => {
    try {
      await api.put(`/leave-requests/${leaveId}/admin-review`, { action });
      toast.success(`Leave request ${action}d!`);
      fetchLeaveRequests(selectedEmployee, fromDate, toDate);
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal review request');
    }
  };

  const viewDocument = async (leaveId) => {
    try {
      const token = localStorage.getItem('token');
      const url = `${BACKEND_URL}/api/leave-requests/${leaveId}/document?token=${token}`;
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Gagal buka dokumen');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved_supervisor: 'bg-blue-100 text-blue-800',
      rejected_supervisor: 'bg-red-100 text-red-800',
      approved: 'bg-emerald-100 text-emerald-800',
      rejected: 'bg-red-100 text-red-800'
    };

    const icons = {
      pending: <Clock className="w-3.5 h-3.5" />,
      approved_supervisor: <CheckCircle className="w-3.5 h-3.5" />,
      rejected_supervisor: <XCircle className="w-3.5 h-3.5" />,
      approved: <CheckCircle className="w-3.5 h-3.5" />,
      rejected: <XCircle className="w-3.5 h-3.5" />
    };

    const labels = {
      pending: 'Pending',
      approved_supervisor: 'Approved by Supervisor',
      rejected_supervisor: 'Rejected by Supervisor',
      approved: 'Approved',
      rejected: 'Rejected'
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[status]}`}>
        {icons[status]}
        {labels[status]}
      </span>
    );
  };

  const combinedRequests = [...pendingApprovals, ...requests.filter(r => !pendingApprovals.some(p => p.id === r.id))];
  const filteredRequests = combinedRequests.filter(r => {
    if (selectedEmployee !== 'all' && r.user_id !== selectedEmployee) return false;
    if (fromDate && r.start_date < fromDate) return false;
    if (toDate && r.start_date > toDate) return false;
    return true;
  });

  if (loading && requests.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A3C34]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="pb-2 border-b border-slate-200/70">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 tracking-tight" data-testid="leave-requests-page-title">Leave Requests</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {user?.role === 'admin'
                ? 'Review and approve leave requests'
                : user?.has_subordinates
                ? 'Review subordinate leave requests and view your own'
                : 'View your leave requests'}
            </p>
          </div>
          {canCreateLeave && (
            <Button
              onClick={() => {
                if (showForm) {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData(emptyFormData());
                  setDocumentFileName('');
                } else {
                  setShowForm(true);
                }
              }}
              data-testid="add-leave-request-button"
              className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]"
            >
              {showForm ? <X className="w-5 h-5 mr-2" /> : <Plus className="w-5 h-5 mr-2" />}
              {showForm ? 'Tutup Form' : 'Ajukan Leave Request'}
            </Button>
          )}
        </div>
      </div>

      {showForm && canCreateLeave ? (
        <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
          <div className="pb-4 border-b border-slate-100 mb-6">
            <h2 className="text-xl font-bold font-heading text-slate-900">
              {editingId ? 'Edit Leave Request' : 'Ajukan Leave Request Baru'}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {editingId ? 'Perbarui detail leave request Anda' : 'Isi detail cuti atau sakit Anda'}
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="leave_type" className="text-sm font-medium text-slate-700">Tipe Leave</Label>
                <Select value={formData.leave_type} onValueChange={(val) => setFormData({ ...formData, leave_type: val })}>
                  <SelectTrigger id="leave_type" className="bg-white border-slate-200 rounded-xl text-sm">
                    <SelectValue placeholder="Pilih tipe leave" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cuti">Cuti</SelectItem>
                    <SelectItem value="sakit">Sakit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="start_date" className="text-sm font-medium text-slate-700">Tanggal Mulai</Label>
                <div className="relative">
                  <Input
                    id="start_date"
                    data-testid="leave-start-date-input"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    className="w-full border-slate-200 rounded-xl text-sm pr-10 focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
                  />
                  <Calendar className="w-4 h-4 text-slate-400 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date" className="text-sm font-medium text-slate-700">Tanggal Selesai</Label>
                <div className="relative">
                  <Input
                    id="end_date"
                    data-testid="leave-end-date-input"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    className="w-full border-slate-200 rounded-xl text-sm pr-10 focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
                  />
                  <Calendar className="w-4 h-4 text-slate-400 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason" className="text-sm font-medium text-slate-700">Alasan</Label>
              <Textarea
                id="reason"
                data-testid="leave-reason-input"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                required
                placeholder="Jelaskan alasan cuti atau sakit Anda..."
                rows={4}
                className="border-slate-200 rounded-xl text-sm placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="document" className="text-sm font-medium text-slate-700">Dokumen (Opsional)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="document"
                  data-testid="leave-document-input"
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.png,.jpg,.jpeg"
                  className="flex-1 border-slate-200 rounded-xl text-sm file:bg-slate-100 file:border-0 file:rounded-md file:px-3 file:py-1 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-200"
                />
              </div>
              {documentFileName && (
                <p className="text-xs text-slate-600">File: {documentFileName}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                disabled={submitting}
                data-testid="leave-submit-button"
                className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99] disabled:opacity-60"
              >
                {submitting ? 'Mengirim...' : editingId ? 'Update Request' : 'Ajukan Request'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData(emptyFormData());
                  setDocumentFileName('');
                }}
                className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
              >
                Batal
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {/* Pending Approvals & Daftar Leave Request - Unified Table */}
      {!showForm && (
      <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
        <div className="flex justify-between items-center flex-wrap gap-4 pb-5 border-b border-slate-100 mb-6">
          <div>
            <h2 className="text-xl font-bold font-heading text-slate-900" data-testid="leave-list-title">Daftar Leave Request</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Rekap seluruh pengajuan cuti dan sakit</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, fromDate: e.target.value }))}
                className="w-[160px] bg-white border-slate-200 rounded-xl text-sm"
                data-testid="leave-from-date-input"
              />
              <span className="text-muted-foreground text-xs font-medium">s/d</span>
              <Input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, toDate: e.target.value }))}
                className="w-[160px] bg-white border-slate-200 rounded-xl text-sm"
                data-testid="leave-to-date-input"
              />
            </div>
            {canFilterByEmployee && (
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger className="w-[200px] bg-white border-slate-200 rounded-xl text-sm" data-testid="leave-employee-filter-select">
                  <SelectValue placeholder="Filter Employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Employee</SelectItem>
                  {user?.has_subordinates && (
                    <SelectItem value={user.id}>{user.name}</SelectItem>
                  )}
                  {employees.map((emp) => (
                    <SelectItem key={emp.user_id} value={emp.user_id}>
                      {emp.user_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Karyawan</th>
                <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Tipe</th>
                <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Tanggal Mulai</th>
                <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Tanggal Selesai</th>
                <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Alasan</th>
                <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Status</th>
                <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Dokumen</th>
                <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Edit / Hapus</th>
                {canApprove && (
                  <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">Approval</th>
                )}
              </tr>
            </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={canApprove ? 9 : 8} className="py-10 text-center text-muted-foreground text-sm">
                      Memuat...
                    </td>
                  </tr>
                ) : filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={canApprove ? 9 : 8} className="py-10 text-center text-muted-foreground text-sm">
                      Tidak ada leave request
                    </td>
                  </tr>
                ) : (
                filteredRequests.map((request, idx) => (
                  <tr
                    key={request.id}
                    data-testid={`leave-request-row-${request.id}`}
                    className={`border-b border-slate-100 last:border-b-0 hover:bg-[#1A3C34]/5 transition-colors ${
                      idx % 2 === 1 ? 'bg-slate-50/60' : 'bg-white'
                    }`}
                  >
                    <td className="py-3 px-4 text-sm font-medium text-slate-800">{request.user_name}</td>
                    <td className="py-3 px-4 text-sm text-slate-700">
                      <span className="inline-flex items-center px-2 py-1 rounded-lg bg-slate-100 text-slate-600 text-xs font-medium">
                        {request.leave_type.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">{new Date(request.start_date).toLocaleDateString('id-ID')}</td>
                    <td className="py-3 px-4 text-sm text-slate-700">{new Date(request.end_date).toLocaleDateString('id-ID')}</td>
                    <td className="py-3 px-4 text-sm max-w-xs text-slate-600">{request.reason.substring(0, 50)}{request.reason.length > 50 ? '...' : ''}</td>
                    <td className="py-3 px-4 text-sm">{getStatusBadge(request.status)}</td>
                    <td className="py-3 px-4 text-sm text-center">
                      {request.document_path ? (
                        <Button
                          onClick={() => viewDocument(request.id)}
                          size="icon"
                          variant="ghost"
                          title="Lihat Dokumen"
                          className="h-8 w-8 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-center">
                      {canEditLeave(request) ? (
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            onClick={() => handleEdit(request)}
                            size="icon"
                            variant="ghost"
                            title="Edit"
                            className="h-8 w-8 rounded-lg text-slate-500 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(request)}
                            size="icon"
                            variant="ghost"
                            title="Hapus"
                            className="h-8 w-8 rounded-lg text-slate-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    {canApprove && (
                      <td className="py-3 px-4 text-sm text-center">
                        {(pendingApprovals.find(p => p.id === request.id) || request.status === 'pending') && request.user_id !== user?.id ? (
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              onClick={() => (user?.role === 'admin' ? handleAdminReview : handleSupervisorReview)(request.id, 'approve')}
                              size="sm"
                              className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-medium px-3"
                            >
                              Approve
                            </Button>
                            <Button
                              onClick={() => (user?.role === 'admin' ? handleAdminReview : handleSupervisorReview)(request.id, 'reject')}
                              size="sm"
                              className="h-8 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium px-3"
                            >
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
      )}
    </div>
  );
}